import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import estadosBrasil from '../data/estadosBrasil';
import { TIPOS_CONSTRUCAO, PADROES_CONSTRUCAO } from '../data/etapasObra';
import { pdfToImage, isPdf } from '../utils/pdfToImage';

const ANALYSIS_STEPS = [
  'Processando planta...',
  'Lendo a planta...',
  'Identificando cômodos...',
  'Calculando áreas...',
  'Detectando itens especiais...',
  'Finalizando análise...',
];

export default function OrcamentoPlanta() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') || 'cub';
  const isCub = tipo === 'cub';

  const fileInputRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tipoConstrucao, setTipoConstrucao] = useState('');
  const [padrao, setPadrao] = useState('');
  const [estado, setEstado] = useState('');
  const [nomeProjeto, setNomeProjeto] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusBanner, setStatusBanner] = useState(null); // { tipo: 'warning'|'error', msg: '' }
  const [analysisStep, setAnalysisStep] = useState(0);
  const [temPlantaEstrutural, setTemPlantaEstrutural] = useState(null); // null = not answered, true/false
  const [fileEstrutural, setFileEstrutural] = useState(null);
  const [previewEstrutural, setPreviewEstrutural] = useState(null);
  const fileEstruturalRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Progress steps during analysis
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setAnalysisStep((prev) => (prev < ANALYSIS_STEPS.length - 1 ? prev + 1 : prev));
    }, 2500);
    return () => clearInterval(interval);
  }, [loading]);

  const brunoMessage = isCub
    ? 'Ótimo! Para a estimativa rápida, você pode enviar uma foto da planta, croqui ou até um esboço. Não precisa ser técnico!'
    : 'Perfeito! Para o orçamento detalhado, envie as plantas arquitetônica, estrutural, hidráulica e elétrica em PDF.';

  const convertToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(f);
  });

  const handleFileSelection = (selectedFile) => {
    setError('');
    const allowedTypes = ['application/pdf', 'image/png'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Apenas PDF e PNG são aceitos. Para melhor precisão, evite JPG.');
      return;
    }
    if (selectedFile.size > maxSize) {
      setError('Arquivo deve ter no máximo 10MB.');
      return;
    }

    setFile(selectedFile);
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview('pdf');
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEstruturalFile = (selectedFile) => {
    const allowedTypes = ['application/pdf', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type) || selectedFile.size > 10 * 1024 * 1024) return;
    setFileEstrutural(selectedFile);
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewEstrutural(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreviewEstrutural('pdf');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!tipoConstrucao) { setError('Selecione o tipo de construção.'); return; }
    if (!padrao) { setError('Selecione o padrão de construção.'); return; }
    if (!estado) { setError('Selecione o estado.'); return; }

    setLoading(true);
    setAnalysisStep(0);
    setStatusBanner(null);

    // Generate unique ID for this orcamento
    const orcamentoId = `orc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Store user selections
    const userConfig = {
      tipoConstrucao, padrao, estado, fluxo: tipo, orcamentoId,
      nomeProjeto: nomeProjeto.trim() || null,
      temPlantaEstrutural: !isCub ? temPlantaEstrutural : null,
    };

    if (file) {
      // Has file → call Vision API
      try {
        let base64 = await convertToBase64(file);
        let mimeType = file.type;

        // PDF → PNG: converter no frontend para que o backend receba
        // imagem de alta resolução e o Passe 0 consiga croppar
        if (isPdf(file.type)) {
          console.log('[PDF→PNG] Convertendo PDF para PNG...');
          const result = await pdfToImage(file, {
            scale: 2, // 144 DPI — suficiente para cotas legíveis
            onProgress: (msg) => console.log(`[PDF→PNG] ${msg}`),
          });
          base64 = result.base64;
          mimeType = 'image/png';
          console.log(`[PDF→PNG] Convertido: ${result.width}x${result.height}px, ${result.numPages} página(s)`);
        }

        const { data } = await api.post('/api/orcamento/analisar', {
          file: base64,
          mimeType,
          tipo: tipoConstrucao,
          padrao,
        }, { timeout: 600000 });

        // Save analysis + config to localStorage
        const analysisData = {
          ...data,
          config: userConfig,
          analyzedAt: new Date().toISOString(),
        };
        localStorage.setItem('orcai_analise_planta', JSON.stringify(analysisData));

        // Redirect to QCO Plan (fullscreen editor)
        navigate(`/qco-plan?tipo=${tipo}&id=${orcamentoId}`);
      } catch (err) {
        console.error('Analysis error:', err);
        const respData = err.response?.data;
        const codigo = respData?.codigo;
        const mensagem = respData?.error || 'Erro ao analisar a planta. Tente novamente.';

        // Timeout do axios (10 min)
        if (err.code === 'ECONNABORTED') {
          setStatusBanner({
            tipo: 'warning',
            msg: 'A análise está demorando mais que o esperado. Tente novamente.',
          });
          setError('A análise está demorando mais que o esperado. Tente novamente.');
          setLoading(false);
          return;
        }

        // Banner de status baseado no código do backend
        if (codigo === 'API_SOBRECARREGADA') {
          setStatusBanner({
            tipo: 'warning',
            msg: 'Nossos servidores de IA estão com alta demanda. Aguarde alguns minutos e tente novamente.',
          });
        } else if (codigo === 'LIMITE_API') {
          setStatusBanner({
            tipo: 'warning',
            msg: 'Sistema de análise temporariamente indisponível. Tente novamente em alguns minutos.',
          });
        } else if (err.response?.status >= 500) {
          setStatusBanner({
            tipo: 'error',
            msg: 'Nossos servidores estão com instabilidade. Estamos trabalhando para normalizar.',
          });
        }

        setError(mensagem);
        setLoading(false);
      }
    } else {
      // No file → save config only with empty analysis, go to confirmation
      const analysisData = {
        area_total: 0,
        comodos: [],
        itens_especiais: [],
        itens_detectados: [],
        itens_obrigatorios: [],
        observacoes: ['Nenhuma planta foi enviada. Preencha os dados manualmente.'],
        config: userConfig,
        analyzedAt: new Date().toISOString(),
      };
      localStorage.setItem('orcai_analise_planta', JSON.stringify(analysisData));

      if (isCub) {
        navigate(`/orcamento/confirmar?tipo=cub&id=${orcamentoId}`);
      } else {
        navigate(`/orcamento/confirmar?tipo=sinapi&id=${orcamentoId}`);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <Header />

      {/* Banner de status da API */}
      {statusBanner && (
        <div className={`fixed top-16 left-0 right-0 z-40 transition-all duration-500 ${
          statusBanner.tipo === 'warning'
            ? 'bg-amber-50 border-b-2 border-amber-400'
            : 'bg-red-50 border-b-2 border-red-400'
        }`}>
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {statusBanner.tipo === 'warning' ? (
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <p className={`text-sm font-medium ${statusBanner.tipo === 'warning' ? 'text-amber-800' : 'text-red-800'}`}>
                {statusBanner.msg}
              </p>
            </div>
            <button
              onClick={() => setStatusBanner(null)}
              className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
                statusBanner.tipo === 'warning' ? 'hover:bg-amber-100 text-amber-600' : 'hover:bg-red-100 text-red-600'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 px-4 pt-32 pb-16">
        <div className="max-w-2xl mx-auto">
          {/* Bruno + speech bubble */}
          <div
            className="flex flex-col items-center mb-8 transition-all duration-700 ease-out"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <img
              src="/images/brand/bruno-principal.png"
              alt="Bruno"
              className="w-20 h-20 md:w-28 md:h-28 object-contain"
            />
            <div className="mt-3 w-full max-w-xl">
              <div className="relative bg-card rounded-2xl shadow-card px-5 py-4 text-center">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-card rotate-45 shadow-card" />
                <p className="relative text-secondary font-medium text-sm md:text-base leading-relaxed">
                  {loading ? ANALYSIS_STEPS[analysisStep] : brunoMessage}
                </p>
              </div>
            </div>
          </div>

          {/* Title badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {isCub ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-primary/10 px-4 py-1.5 rounded-full">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Estimativa Rápida (CUB)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary bg-secondary/10 px-4 py-1.5 rounded-full">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Orçamento Detalhado (SINAPI)
              </span>
            )}
          </div>

          {/* Loading overlay */}
          {loading && (
            <div className="bg-card rounded-2xl shadow-card p-8 mb-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>
              <p className="text-secondary font-semibold mb-2">Analisando sua planta...</p>
              <p className="text-sm text-muted-foreground">{ANALYSIS_STEPS[analysisStep]}</p>
              <div className="mt-4 flex justify-center gap-1.5">
                {ANALYSIS_STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                      i <= analysisStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Form card */}
          {!loading && (
            <div
              className="bg-card rounded-2xl shadow-card p-6 md:p-8 transition-all duration-700 delay-300 ease-out"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nome do Projeto */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">
                    Nome do Projeto (opcional)
                  </label>
                  <input
                    type="text"
                    value={nomeProjeto}
                    onChange={(e) => setNomeProjeto(e.target.value)}
                    placeholder="Ex: Casa da Praia, Reforma Apto 301..."
                    className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-foreground">
                    Planta do Projeto {isCub ? '(opcional)' : '*'}
                  </label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files[0]) handleFileSelection(e.dataTransfer.files[0]); }}
                    className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input ref={fileInputRef} type="file" onChange={(e) => { if (e.target.files?.[0]) handleFileSelection(e.target.files[0]); }} accept=".pdf,.png" className="hidden" />

                    {preview ? (
                      <div className="space-y-3">
                        {preview === 'pdf' ? (
                          <div>
                            <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto">
                              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">{file?.name}</p>
                          </div>
                        ) : (
                          <div>
                            <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                            <p className="text-sm text-muted-foreground mt-2">{file?.name}</p>
                          </div>
                        )}
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} className="text-sm text-destructive hover:text-destructive/80 font-medium">
                          Remover arquivo
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-12 h-12 mx-auto bg-muted rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        </div>
                        <p className="text-foreground font-medium text-sm">Arraste o arquivo aqui</p>
                        <p className="text-muted-foreground text-xs">ou clique para selecionar — PDF ou PNG (máx. 10MB)</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-start gap-2 bg-accent/10 border border-accent/30 rounded-xl px-3 py-2.5 mt-2">
                    <svg className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-xs text-accent leading-relaxed">
                      <p className="font-semibold mb-1">Para maior precisão na leitura pela IA, recomendamos:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>Plantas em formato PNG ou PDF de alta resolução</li>
                        <li>Cotas e textos com fonte legível (mínimo 10pt)</li>
                        <li>Áreas dos cômodos escritas diretamente na planta (ex: A:19,43m²)</li>
                        <li>Plantas cotadas e/ou com escala definida</li>
                      </ul>
                      <p className="mt-1">Arquivos JPG e plantas com texto muito pequeno podem reduzir a acurácia dos resultados.</p>
                    </div>
                  </div>
                </div>

                {/* Grid fields */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Tipo de Construção *</label>
                    <select value={tipoConstrucao} onChange={(e) => setTipoConstrucao(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                      <option value="">Selecione</option>
                      {TIPOS_CONSTRUCAO.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Padrão *</label>
                    <select value={padrao} onChange={(e) => setPadrao(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                      <option value="">Selecione</option>
                      {PADROES_CONSTRUCAO.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Estado *</label>
                    <select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                      <option value="">Selecione</option>
                      {estadosBrasil.map((est) => (<option key={est.sigla} value={est.sigla}>{est.nome}</option>))}
                    </select>
                  </div>
                </div>

                {/* Structural Plan Question (SINAPI only) */}
                {!isCub && (
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-foreground">
                      Você tem a planta estrutural?
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setTemPlantaEstrutural(true)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                          temPlantaEstrutural === true
                            ? 'border-secondary bg-secondary/10 text-secondary'
                            : 'border-border text-muted-foreground hover:border-secondary/50'
                        }`}
                      >
                        Sim
                      </button>
                      <button
                        type="button"
                        onClick={() => { setTemPlantaEstrutural(false); setFileEstrutural(null); setPreviewEstrutural(null); }}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                          temPlantaEstrutural === false
                            ? 'border-secondary bg-secondary/10 text-secondary'
                            : 'border-border text-muted-foreground hover:border-secondary/50'
                        }`}
                      >
                        Não
                      </button>
                    </div>

                    {/* Upload structural plan */}
                    {temPlantaEstrutural === true && (
                      <div
                        className="border-2 border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-secondary/50 transition-colors"
                        onClick={() => fileEstruturalRef.current?.click()}
                      >
                        <input ref={fileEstruturalRef} type="file" onChange={(e) => { if (e.target.files?.[0]) handleEstruturalFile(e.target.files[0]); }} accept=".pdf,.png" className="hidden" />
                        {previewEstrutural ? (
                          <div className="flex items-center justify-center gap-3">
                            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            <span className="text-sm text-foreground">{fileEstrutural?.name}</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setFileEstrutural(null); setPreviewEstrutural(null); }} className="text-xs text-destructive hover:underline">Remover</button>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Envie a planta estrutural (PDF, JPG ou PNG)</p>
                        )}
                      </div>
                    )}

                    {/* Bruno warning for no structural plan */}
                    {temPlantaEstrutural === false && (
                      <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4">
                        <img src="/images/brand/bruno-principal.png" alt="Bruno" className="w-10 h-10 object-contain flex-shrink-0" />
                        <div>
                          <p className="text-sm text-foreground font-medium mb-1">
                            Vou usar premissas típicas para este tipo de construção.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Margem de variação estrutural: ±25%. Recomendo validar com engenheiro.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                    <p className="text-destructive text-sm font-medium">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 font-bold rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-white ${
                    isCub ? 'bg-primary hover:bg-primary/90' : 'bg-secondary hover:bg-secondary/90'
                  } disabled:opacity-50`}
                >
                  Analisar com IA
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </form>
            </div>
          )}

          {/* Back link */}
          {!loading && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/orcamento/novo')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Voltar e escolher outro tipo
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
